import { useCallback, useEffect, useMemo, useState } from "react";

import { usePusherHold } from "@recruiter/shared";

import { basePusherConfig } from "../../config";
import { requestSellerExport, sellerDocUrl } from "../../lib/seller-client";
import { exportKey, useSellerStore } from "../../lib/store/seller-store";

// The PDF half of a document chip. It is a BUTTON, not a link, because there is
// no file to link to until the worker has rendered one: a click POSTs the
// document's `export_url` (202 + turn id), the worker renders off the web cycle,
// and `seller.export.ready` arrives on the talent's Pusher channel with the
// download URL (doctrine 1.1/1.2 — no inline render, no polling).
//
// Never render inline: the old synchronous route took ~9.7s against the 10s
// server timeout and 504'd about half the time.

// One bounded wait, not a poll: Pusher never replays a one-shot event missed
// during a disconnect, so if nothing arrives the button must still leave its
// spinner. 90s is well past a healthy render (~8s) plus a cold browser lease.
const EXPORT_WATCHDOG_MS = 90_000;

function triggerDownload(url: string): void {
  // A programmatic anchor, not window.open: this runs in a Pusher callback with
  // no user gesture behind it, where a popup would be blocked. The route answers
  // with Content-Disposition: attachment, so the page never navigates away.
  const a = document.createElement("a");
  a.href = sellerDocUrl(url);
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function DocExportButton({
  threadId,
  kind,
  sellerId,
  exportUrl,
}: {
  threadId: string;
  /** Which document this chip renders (`brief` / `client`). Part of the store
   *  key: a thread can carry several chips and they must not share a phase. */
  kind: string;
  sellerId: string;
  exportUrl: string;
}) {
  const key = exportKey(threadId, kind);
  const state = useSellerStore((s) => s.exports[key]);
  const startExport = useSellerStore((s) => s.startExport);
  const resolveExport = useSellerStore((s) => s.resolveExport);
  const failExport = useSellerStore((s) => s.failExport);
  const failExportEnqueue = useSellerStore((s) => s.failExportEnqueue);
  const markExportDownloaded = useSellerStore((s) => s.markExportDownloaded);
  // A POST that never reached the queue. Local, not store state: it says nothing
  // about the artifact, so it must not overwrite an export that HAS one.
  const [enqueueError, setEnqueueError] = useState<string | null>(null);

  const rendering = state?.phase === "rendering";

  // Hold the shared socket open for the length of the render. The ready event is
  // ONE SHOT with no REST reconcile behind it (there is no status endpoint —
  // an already-finished export comes back on the POST instead), and the shared
  // connection releases itself when a tab sits hidden and idle for 30s. A tester
  // who switches tabs during an ~8s render would otherwise come back to a button
  // that spun out to the watchdog, and no local test run shows you that.
  const pusherConfig = useMemo(() => basePusherConfig(sellerId), [sellerId]);
  usePusherHold(pusherConfig, rendering);

  // The EVENT path's one-shot latch. It lives in the STORE (`downloaded`), not in
  // a ref: this chip unmounts whenever the talent switches threads, and a
  // per-mount latch would re-fire the download on the way back with no click
  // behind it. Re-read the live entry rather than trusting the render's snapshot
  // so StrictMode's double-invoked effect sees the flag its first pass set.
  useEffect(() => {
    if (state?.phase !== "ready" || !state.url) return;
    const live = useSellerStore.getState().exports[key];
    if (live?.turnId !== state.turnId || live.downloaded) return;
    markExportDownloaded(key, state.turnId);
    triggerDownload(state.url);
  }, [key, markExportDownloaded, state]);

  // Release the spinner if no event ever lands. A later event still resolves the
  // export normally — the store only refuses to downgrade a READY one.
  useEffect(() => {
    if (!rendering || !state) return;
    const turnId = state.turnId;
    const id = window.setTimeout(() => failExport(key, turnId), EXPORT_WATCHDOG_MS);
    return () => window.clearTimeout(id);
  }, [failExport, key, rendering, state]);

  const onClick = useCallback(async (): Promise<void> => {
    if (rendering) return;
    setEnqueueError(null);
    const res = await requestSellerExport(exportUrl, sellerId);
    if (!res.ok) {
      // Nothing was enqueued, so there is no turn to await. The error stays
      // component-local (doctrine 1.5): a failed POST after a successful export
      // still has a real PDF behind it, so the store must not mark it failed.
      // `failExportEnqueue` only marks a chip that holds nothing.
      console.warn("[seller-export] enqueue failed:", res.message);
      setEnqueueError(res.message);
      failExportEnqueue(key);
      return;
    }
    const { turn_id: turnId, url, filename } = res.data;
    startExport(key, turnId);
    if (url) {
      // The bytes already existed and the response carried them, so this click
      // collapsed onto a finished render and there is no event coming — the
      // worker's publish is seq=1-guarded and already fired for this turn.
      // Download straight away (still inside the click's user gesture) and stamp
      // the latch so the effect above does not fire a second copy.
      resolveExport(turnId, { ok: true, url, filename });
      markExportDownloaded(key, turnId);
      triggerDownload(url);
    }
  }, [
    exportUrl,
    failExportEnqueue,
    key,
    markExportDownloaded,
    rendering,
    resolveExport,
    sellerId,
    startExport,
  ]);

  const label =
    state?.phase === "rendering"
      ? "preparing…"
      : state?.phase === "failed"
        ? "PDF failed"
        : state?.phase === "ready"
          ? "PDF ✓"
          : "PDF";

  return (
    <button
      type="button"
      className={`sl-doc__pdf${rendering ? " sl-doc__pdf--busy" : ""}${
        state?.phase === "failed" ? " sl-doc__pdf--error" : ""
      }`}
      onClick={() => void onClick()}
      disabled={rendering}
      title={
        enqueueError
          ? `Couldn't start the PDF: ${enqueueError}`
          : state?.phase === "failed"
            ? "The PDF render failed. Try again, or open the document as a page."
            : "Download this document as a PDF"
      }
    >
      {label}
    </button>
  );
}
